/**
 * FTL bitmap font (`fonts/*.font`) parser and text layout. Pure TS (no DOM, no GL) so it
 * runs in the browser, in node tools and in tests. See re/notes/font-format.md.
 *
 * FTL: font_parse_header, font_parse_charinfo, font_bitmap_init, tex_parse_header,
 *      font_bitmap_render, font_bitmap_text_advance, font_bitmap_get_text_bounds.
 */

/** One glyph. Coordinates are atlas pixels; metrics are in the font's native pixels. */
export interface FontGlyph {
  readonly codepoint: number;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  /** Pixels from the baseline up to the top of the glyph bitmap (signed). */
  readonly ascent: number;
  /** `h - ascent`: pixels from the baseline down to the bottom of the bitmap. */
  readonly descent: number;
  /** Space before the bitmap (8.8 fixed point in the file). */
  readonly lsb: number;
  /** Space after the bitmap (8.8 fixed point in the file). Advance = lsb + w + rsb. */
  readonly rsb: number;
}

/** 8-bit coverage atlas (row 0 = top = v 0). */
export interface FontAtlas {
  readonly width: number;
  readonly height: number;
  /** TEX pixel format byte; every shipped font uses 0x40 (8-bit alpha). */
  readonly format: number;
  readonly pixels: Uint8Array;
}

export interface FontFile {
  /** Header byte 5: the pixel size the atlas was rendered at (the FTL "native size"). */
  readonly nativeSize: number;
  /** Header byte 6: baseline in native pixels from the top of the line. */
  readonly baseline: number;
  /** Largest glyph ascent/descent (font_bitmap_init computes these for get_metrics). */
  readonly maxAscent: number;
  readonly maxDescent: number;
  readonly glyphs: ReadonlyMap<number, FontGlyph>;
  readonly atlas: FontAtlas;
}

const FONT_MAGIC = 0x464f4e54; // "FONT"
const TEX_MAGIC = 0x5445580a; // "TEX\n"
export const TEX_FORMAT_A8 = 0x40;

export function parseFontFile(bytes: Uint8Array): FontFile {
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (bytes.length < 24 || dv.getUint32(0) !== FONT_MAGIC) throw new Error('not an FTL .font file');
  if (bytes[4] !== 1) throw new Error(`unsupported .font version ${bytes[4]}`);
  const nativeSize = bytes[5]!;
  const baseline = bytes[6]!;
  const glyphOffset = dv.getUint32(8);
  const glyphCount = dv.getUint16(12);
  const entrySize = dv.getUint16(14);
  const texOffset = dv.getUint32(16);
  const texSize = dv.getUint32(20);
  if (entrySize !== 16) throw new Error(`unexpected glyph entry size ${entrySize}`);
  if (glyphOffset + glyphCount * 16 > bytes.length || texOffset + texSize > bytes.length) {
    throw new Error('truncated .font file');
  }

  const glyphs = new Map<number, FontGlyph>();
  let maxAscent = 0;
  let maxDescent = 0;
  for (let i = 0; i < glyphCount; i++) {
    const o = glyphOffset + i * 16;
    const codepoint = dv.getUint32(o);
    const w = bytes[o + 8]!;
    const h = bytes[o + 9]!;
    const ascent = dv.getInt8(o + 10);
    // byte o+11 is padding (font_parse_charinfo does not copy it)
    const g: FontGlyph = {
      codepoint,
      x: dv.getUint16(o + 4),
      y: dv.getUint16(o + 6),
      w,
      h,
      ascent,
      descent: h - ascent,
      lsb: dv.getInt16(o + 12) / 256,
      rsb: dv.getInt16(o + 14) / 256,
    };
    if (codepoint > 0x10ffff) continue;
    glyphs.set(codepoint, g);
    if (ascent > maxAscent) maxAscent = ascent;
    if (h - ascent > maxDescent) maxDescent = h - ascent;
  }

  return { nativeSize, baseline, maxAscent, maxDescent, glyphs, atlas: parseTex(bytes.subarray(texOffset, texOffset + texSize)) };
}

/** FTL's "TEX\n" texture container (v2 as used by fonts; v1 parsed for completeness). */
export function parseTex(bytes: Uint8Array): FontAtlas {
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (bytes.length < 32 || dv.getUint32(0) !== TEX_MAGIC) throw new Error('missing TEX header');
  const version = bytes[4];
  let format: number;
  let mipLevels: number;
  let palette: number;
  if (version === 2) {
    format = bytes[5]!;
    mipLevels = bytes[6]! + 1;
    palette = bytes[7]!;
  } else if (version === 1) {
    format = bytes[13]!;
    mipLevels = bytes[14]! + 1;
    palette = bytes[15]!;
  } else {
    throw new Error(`unsupported TEX version ${version}`);
  }
  const width = dv.getUint16(8);
  const height = dv.getUint16(10);
  const dataOffset = dv.getUint32(16);
  const dataSize = dv.getUint32(20);
  if (format !== TEX_FORMAT_A8 || palette !== 0) {
    throw new Error(`unsupported TEX format 0x${format.toString(16)} (palette ${palette})`);
  }
  if (dataSize < width * height || dataOffset + width * height > bytes.length) throw new Error('truncated TEX data');
  void mipLevels; // only level 0 is used
  return { width, height, format, pixels: bytes.subarray(dataOffset, dataOffset + width * height) };
}

// --- layout -----------------------------------------------------------------

/** C roundf: halves round away from zero (Math.round rounds -2.5 to -2). */
function roundf(v: number): number {
  return v < 0 ? -Math.round(-v) : Math.round(v);
}

export interface GlyphQuad {
  glyph: FontGlyph;
  /** Destination rect (pixels, y down). */
  x: number;
  y: number;
  w: number;
  h: number;
}

export type TextAlign = 'left' | 'center' | 'right';

/**
 * Lays out one line with its baseline at `baselineY`, exactly like font_bitmap_render with
 * antialias off (FTL disables it for every bitmap font): each glyph's left edge is
 * `round(pen + lsb*scale)`, the pen then moves by `(w + rsb)*scale`. Codepoints without a
 * glyph are skipped (no advance). Returns the pen position after the last glyph.
 */
export function layoutLine(
  font: FontFile,
  text: string,
  scale: number,
  x: number,
  baselineY: number,
  out: GlyphQuad[] | null,
): number {
  let pen = x;
  for (const ch of text) {
    const g = font.glyphs.get(ch.codePointAt(0)!);
    if (!g) continue;
    const x0 = roundf(pen + g.lsb * scale);
    if (out && g.w > 0 && g.h > 0) {
      out.push({ glyph: g, x: x0, y: baselineY - g.ascent * scale, w: g.w * scale, h: g.h * scale });
    }
    pen = x0 + (g.w + g.rsb) * scale;
  }
  return pen;
}

/** Width of a single line. FTL: font_bitmap_text_advance (rounded). */
export function lineAdvance(font: FontFile, text: string, scale: number): number {
  let x = 0;
  for (const ch of text) {
    const g = font.glyphs.get(ch.codePointAt(0)!);
    if (!g) continue;
    x = roundf(x + g.lsb * scale);
    x += (g.w + g.rsb) * scale;
  }
  return roundf(x);
}

/** Horizontal ink bounds of a line. FTL: font_bitmap_get_text_bounds (min starts at 0). */
export function lineBounds(font: FontFile, text: string, scale: number): { min: number; max: number } {
  let min = 0;
  let max = 0;
  let pen = 0;
  for (const ch of text) {
    const g = font.glyphs.get(ch.codePointAt(0)!);
    if (!g) continue;
    const left = roundf(pen + g.lsb * scale);
    const right = roundf(pen + g.lsb * scale + g.w * scale);
    if (left < min) min = left;
    if (right > max) max = right;
    pen = left + (g.w + g.rsb) * scale;
  }
  return { min, max };
}

/** Pen x for a line drawn at `x` with the given alignment. FTL: font_create_text. */
export function alignLineX(font: FontFile, text: string, scale: number, x: number, align: TextAlign): number {
  if (align === 'center') {
    const b = lineBounds(font, text, scale);
    return x - (b.min + b.max) * 0.5;
  }
  if (align === 'right') return x - lineAdvance(font, text, scale);
  return x;
}

/** Advance of one codepoint (font_bitmap_char_advance, rounded). 0 when missing. */
export function charAdvance(font: FontFile, codepoint: number, scale: number): number {
  const g = font.glyphs.get(codepoint);
  return g ? roundf((g.w + g.lsb + g.rsb) * scale) : 0;
}

/**
 * Splits text into lines: hard breaks on '\n', and when `maxWidth > 0` greedy word wrap
 * that sums per-character advances like freetype::formatLines. A line overflows only if a
 * character pushes it past maxWidth; the break goes at the last space (spaces at the break
 * are dropped); a single word longer than the line is split before the overflowing char.
 * Simplification: FTL's CJK kinsoku rules (freetype::canBreakLine) are not reproduced; CJK
 * text breaks anywhere.
 */
export function wrapLines(font: FontFile, text: string, scale: number, maxWidth: number): string[] {
  const out: string[] = [];
  for (const para of text.split('\n')) {
    if (!(maxWidth > 0)) {
      out.push(para);
      continue;
    }
    const chars = Array.from(para);
    let start = 0; // index of the first char of the current line
    let width = 0;
    let lastSpace = -1;
    for (let i = 0; i < chars.length; i++) {
      const ch = chars[i]!;
      const adv = charAdvance(font, ch.codePointAt(0)!, scale);
      if (ch === ' ') {
        lastSpace = i;
      } else if (width + adv > maxWidth && i > start) {
        if (lastSpace >= start) {
          out.push(chars.slice(start, lastSpace).join(''));
          start = lastSpace;
          while (start < i && chars[start] === ' ') start++;
          width = 0;
          for (let j = start; j < i; j++) width += charAdvance(font, chars[j]!.codePointAt(0)!, scale);
        } else {
          out.push(chars.slice(start, i).join(''));
          start = i;
          width = 0;
        }
        lastSpace = -1;
      }
      width += adv;
    }
    out.push(chars.slice(start).join(''));
  }
  return out;
}

// --- FTL font numbers ---------------------------------------------------------

/**
 * One entry of FTL's font table: `freetype::easy_print(int font, ...)` looks the number up
 * in ResourceControl's map. The same .font file is registered several times at integer
 * multiples of its native size (pixel-doubled big titles).
 * FTL: ResourceControl::PreloadFont (bitmap branch), freetype::font_data::init_bitmap.
 */
export interface FtlFontSpec {
  readonly id: number;
  /** Path inside ftl.dat. */
  readonly file: string;
  /** Render size in px; scale = size / nativeSize. */
  readonly size: number;
  /** freetype::font_data.baseline: px from the print y down to the baseline. */
  readonly baseline: number;
  /** freetype::font_data.lineHeight: px between consecutive lines. */
  readonly lineHeight: number;
}

const spec = (id: number, file: string, size: number, baseline: number, lineHeight: number): FtlFontSpec => ({
  id,
  file: `fonts/${file}.font`,
  size,
  baseline,
  lineHeight,
});

/** FTL font number → font. Values resolved from PreloadFont (see re/notes/font-format.md). */
export const FTL_FONTS: readonly FtlFontSpec[] = [
  spec(0, 'num_font', 24, 12, 15), // FONT_NUM
  spec(5, 'c&c', 15, 10, 11),
  spec(9, 'JustinFont8', 19, 12, 15),
  spec(10, 'JustinFont10', 19, 12, 17),
  spec(12, 'JustinFont11Bold', 19, 12, 20),
  spec(13, 'JustinFont12Bold', 19, 12, 22),
  spec(14, 'c&cnew', 38, 24, 34),
  spec(16, 'c&cnew', 38, 24, 20),
  spec(18, 'JustinFont11Bold', 38, 24, 34),
  spec(20, 'JustinFont11Bold', 57, 36, 53),
  spec(24, 'c&cnew', 57, 36, 53),
  spec(51, 'HL1', 11, 8, 10), // FONT_HL1
  spec(52, 'HL1', 22, 16, 24), // FONT_HL1_2X
  spec(53, 'HL1', 33, 24, 38), // FONT_HL1_3X
  spec(59, 'HL1', 22, 16, 18), // FONT_HL1_2X_SHORT
  spec(61, 'HL2', 12, 8, 10), // FONT_HL2
  spec(62, 'HL2', 24, 16, 24), // FONT_HL2_2X
  spec(63, 'HL2', 36, 24, 38), // FONT_HL2_3X
  spec(64, 'HL2', 48, 32, 52), // FONT_HL2_4X
];

/** Japanese fonts (only used when the game language is ja; files are 0.6-17 MB). */
export const FTL_FONTS_JA: readonly FtlFontSpec[] = [
  spec(100, 'ja/misaki_gothic', 8, 8, 4), // FONT_JA_MISAKI
  spec(101, 'ja/mplus10', 12, 10, 8), // FONT_JA_MPLUS_10
  spec(102, 'ja/mplus12', 14, 12, 10), // FONT_JA_MPLUS_12
  spec(103, 'ja/DotGothic-24', 32, 22, 28), // FONT_JA_DOTGOTHIC_24
  spec(104, 'ja/DotGothic-32', 44, 29, 40), // FONT_JA_DOTGOTHIC_32
  spec(105, 'ja/DotGothic-16-O', 22, 17, 18), // FONT_JA_DOTGOTHIC_16_OUTLINE
  spec(106, 'ja/DotGothic-24-O', 32, 24, 28), // FONT_JA_DOTGOTHIC_24_OUTLINE
  spec(107, 'ja/DotGothic-32-O', 43, 32, 39), // FONT_JA_DOTGOTHIC_32_OUTLINE
];

/**
 * Font number remapping done by ResourceControl::GetFontData before the lookup:
 * 6, 7, 8 → 9 and 11 → 12; numbers that are not registered fall back to 12.
 */
export function resolveFtlFontId(id: number, known: (id: number) => boolean): number {
  if (id >= 6 && id <= 8) id = 9;
  else if (id === 11) id = 12;
  return known(id) ? id : 12;
}
